import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);

  if (!stats) return (<div className="min-h-screen bg-[var(--pf-bg)]"><Nav/><div className="p-12 pf-label">Loading…</div></div>);

  const statusData = Object.entries(stats.orders_by_status).map(([k,v]) => ({name: k.replace('_',' '), value: v}));
  const productData = Object.entries(stats.orders_by_product).map(([k,v]) => ({name: k.replace('_',' '), value: v}));
  const colors = ["#09090B", "#E01A4F", "#00E5FF", "#FFD700", "#52525B"];

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="pf-label mb-3">Admin · Control Room</div>
        <h1 className="text-5xl font-black tracking-tighter mb-10">Overview.</h1>

        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[var(--pf-border)] mb-10">
          {[
            ["Clients", stats.total_clients],
            ["Briefs", stats.total_briefs],
            ["Orders", stats.total_orders],
            ["Revenue", `₹${stats.revenue_inr.toLocaleString()}`],
          ].map(([l,v]) => (
            <div key={l} className="border-r border-b border-[var(--pf-border)] p-6" data-testid={`admin-stat-${l.toLowerCase()}`}>
              <div className="pf-label">{l}</div>
              <div className="font-mono text-3xl font-black mt-2">{v}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="border border-[var(--pf-border)] p-6">
            <div className="pf-label mb-4">Orders by status</div>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={statusData}>
                  <XAxis dataKey="name" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} />
                  <Bar dataKey="value">
                    {statusData.map((_, i) => <Cell key={i} fill={colors[i%colors.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="border border-[var(--pf-border)] p-6">
            <div className="pf-label mb-4">Orders by product</div>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={productData}>
                  <XAxis dataKey="name" tick={{fontSize:11}} />
                  <YAxis tick={{fontSize:11}} />
                  <Bar dataKey="value">
                    {productData.map((_, i) => <Cell key={i} fill={colors[i%colors.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/admin/assignments" className="border-2 border-[var(--pf-brand)] p-6 hover:bg-[var(--pf-brand)]/5 transition-colors" data-testid="admin-assignments-link">
            <div className="pf-label text-[var(--pf-brand)]">Route</div>
            <div className="text-2xl font-black mt-2">Assignments →</div>
          </Link>
          <Link to="/admin/orders" className="border border-[var(--pf-border)] p-6 hover:bg-white transition-colors" data-testid="admin-orders-link">
            <div className="pf-label">Manage</div>
            <div className="text-2xl font-black mt-2">Orders pipeline →</div>
          </Link>
          <Link to="/admin/vendors" className="border border-[var(--pf-border)] p-6 hover:bg-white transition-colors" data-testid="admin-vendors-link">
            <div className="pf-label">Manage</div>
            <div className="text-2xl font-black mt-2">Vendor network →</div>
          </Link>
          <Link to="/admin/whatsapp" className="border-2 border-[#25D366] p-6 hover:bg-[#25D366]/5 transition-colors" data-testid="admin-whatsapp-link">
            <div className="pf-label text-[#25D366]">Channel</div>
            <div className="text-2xl font-black mt-2">WhatsApp bot →</div>
          </Link>
          <Link to="/admin/team" className="border border-[var(--pf-border)] p-6 hover:bg-white transition-colors md:col-span-2 lg:col-span-4" data-testid="admin-team-link">
            <div className="pf-label">Invite-only access</div>
            <div className="text-2xl font-black mt-2">Team & invitations →</div>
            <div className="text-xs text-[var(--pf-text-2)] mt-2">Send email + WhatsApp invitations to designers, print vendors, and agency admins.</div>
          </Link>
        </div>
      </div>
    </div>
  );
}
